/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --nve-sys-interaction-state-base: var(--background, var(--nve-sys-layer-canvas-background));
  --background: var(--nve-sys-interaction-background);
  --color: var(--nve-sys-interaction-color);
  --padding: 0 var(--nve-ref-size-300);
  --border: 0;
  --border-radius: var(--nve-ref-border-radius-full);
  --font-weight: var(--nve-ref-font-weight-semibold);
  --font-size: var(--nve-ref-font-size-100);
  --text-decoration: none;
  --text-align: center;
  --cursor: pointer;
  --gap: var(--nve-ref-space-xs);
  --height: var(--nve-sys-interaction-field-height);
  --text-transform: capitalize;
  --line-height: 1.1em;
  --_icon-color: var(--color);
  --width: initial;
  --min-width: var(--width);
  display: inline-flex;
  position: relative;
  user-select: none;
  white-space: nowrap;
  width: var(--width);
  min-width: var(--min-width);
}

[internal-host] {
  color: var(--color);
  background: var(--background);
  padding: var(--padding);
  height: var(--height);
  width: 100%;
  border: var(--border);
  border-radius: var(--border-radius);
  text-decoration: var(--text-decoration);
  text-align: var(--text-align);
  font-weight: var(--font-weight);
  font-size: var(--font-size);
  cursor: var(--cursor);
  gap: var(--gap);
  text-transform: var(--text-transform);
  display: inline-flex;
  place-items: center;
  justify-content: center;
  white-space: nowrap;
  line-height: var(--line-height);
  overflow: hidden;
}

::slotted(nve-icon:not([status])) {
  --color: var(--_icon-color);
  pointer-events: none;
}

::slotted(a) {
  text-decoration: var(--text-decoration) !important;
  color: var(--color) !important;
  gap: var(--gap) !important;
  display: flex !important;
  align-items: center !important;
  outline: 0;
}

::slotted(a)::after {
  display: block;
  content: ' ';
  inset: 0;
  position: absolute;
}

:host([nve-control]) {
  --text-align: start;
  --border-radius: var(--nve-ref-border-radius-sm);
  --font-weight: var(--nve-ref-font-weight-regular);
  --padding: 0 var(--nve-ref-size-200);
}

:host([nve-control]) [internal-host] {
  justify-content: start;
}

:host([nve-control]) ::slotted([name='chevron-down']) {
  --color: var(--nve-sys-text-muted-color);
  margin-inline: auto 0;
}

:host([nve-control]:hover) [interaction-state] {
  --nve-sys-interaction-state-ratio: 0;
}

:host([size='sm']) {
  --height: var(--nve-ref-size-700);
}

:host([size='lg']) {
  --height: var(--nve-ref-size-900);
  --padding: 0 var(--nve-ref-size-400);
  --font-size: var(--nve-ref-font-size-200);
  --line-height: 1em;
}

:host([readonly]) {
  --text-decoration: none;
  --cursor: cursor;
}

:host([interaction='emphasis']) {
  --color: var(--nve-sys-interaction-emphasis-color);
  --background: var(--nve-sys-interaction-emphasis-background);
}

:host([interaction='destructive']) {
  --color: var(--nve-sys-interaction-destructive-color);
  --background: var(--nve-sys-interaction-destructive-background);
}

:host(:state(disabled)) {
  --color: var(--nve-sys-interaction-disabled-color);
  --background: var(--nve-sys-interaction-disabled-background);
  --cursor: not-allowed;
}

:host(:state(disabled)) ::slotted(a) {
  cursor: not-allowed;
}

:host(:state(pressed):not([interaction])) {
  --color: var(--nve-sys-interaction-selected-color);
}

:host(:state(pressed):not([interaction], [container])) {
  --background: var(--nve-sys-support-accent-color);
  --color: var(--nve-sys-text-white-color);
}

:host(:hover:not([interaction], [disabled], [readonly], [pressed])) {
  --color: var(--nve-sys-interaction-hover-color);
}

:host([container='flat'][selected]) [interaction-state],
:host([container='flat']:state(selected)) [interaction-state],
:host([container='flat'][pressed]) [interaction-state],
:host([container='flat']:state(pressed)) [interaction-state] {
  --nve-sys-interaction-state-ratio: var(--nve-sys-interaction-state-ratio-selected);
}

:host([container*='flat']),
:host([container*='inline']) {
  --background: transparent;
  --text-transform: initial;
  --border-radius: var(--nve-ref-border-radius-xs);
  --_icon-color: var(--color);
}

:host([container*='flat'][interaction]:hover) {
  --background: color-mix(
    in oklab,
    var(--color) var(--nve-sys-contrast-mute-ratio),
    var(--nve-sys-contrast-mute-color)
  );
}

@container style(--nve-sys-color-scheme: dark) {
  :host([container*='flat']) {
    --nve-sys-interaction-state-ratio-selected: 16% !important;
    --nve-sys-interaction-state-ratio-hover: 12% !important;
  }
}

:host([container*='flat'][interaction='emphasis']) {
  --color: var(--nve-sys-interaction-emphasis-background);
}

:host([container*='flat'][interaction='destructive']) {
  --color: color-mix(in oklab, var(--nve-sys-interaction-destructive-background) 100%, currentColor 20%);
}

:host([container*='inline']) {
  --padding: 0;
  --height: fit-content;
  --line-height: 0.8;

  [internal-host] {
    overflow: initial;
  }
}

:host([container*='inline']:hover) {
  --background: transparent;
  --text-decoration: underline;
}

:host([container*='inline']:hover) [interaction-state] {
  --nve-sys-interaction-state-ratio: 0;
}

:host([container*='inline']:state(active)) {
  --background: transparent;
  --text-decoration: underline;
}

:host([container*='flat']:state(disabled)),
:host([container*='inline']:state(disabled)) {
  --color: var(--nve-sys-interaction-disabled-color);
  --background: transparent;
  --text-decoration: none !important;
}

:host([container*='flat']:state(disabled)) [interaction-state],
:host([container*='inline']:state(disabled)) [interaction-state] {
  --nve-sys-interaction-state-ratio: 0;
}

:host([container*='inline']:state(selected)) {
  --color: var(--nve-sys-interaction-selected-color);
  --background: transparent;
}

:host([container*='flat']:state(selected)) {
  --color: var(--nve-sys-interaction-selected-color);
}

:host([container*='flat'][interaction='emphasis']:state(selected)) {
  --color: var(--nve-sys-interaction-emphasis-background);
  --background: color-mix(
    in oklab,
    var(--color) var(--nve-sys-contrast-mute-ratio),
    var(--nve-sys-contrast-mute-color)
  );
}

:host([container*='flat'][interaction='destructive']:state(selected)) {
  --color: var(--nve-sys-interaction-destructive-background);
  --background: color-mix(
    in oklab,
    var(--color) var(--nve-sys-contrast-mute-ratio),
    var(--nve-sys-contrast-mute-color)
  );
}

:host(:state(selected)) {
  --color: var(--nve-sys-text-emphasis-color);
}

:host([container*='inline']:state(selected)) [interaction-state] {
  --nve-sys-interaction-state-ratio: 0;
}

:host([container*='inline'][readonly]:not(:state(anchor))) {
  --text-decoration: none;
  --cursor: cursor;
}

:host([container='inline'][interaction='destructive']) {
  --color: color-mix(in oklab, var(--nve-sys-interaction-destructive-background) 100%, currentColor 20%);
}

:host([container='inline'][interaction='emphasis']) {
  --color: var(--nve-sys-interaction-emphasis-background);
}

@keyframes inline-underline {
  from {
    transform: translateX(-50%) scaleX(0);
  }

  to {
    transform: translateX(-50%) scaleX(1);
  }
}

:host([container*='inline']) [internal-host]::after {
  content: '';
  position: absolute;
  left: 50%;

  /* private API for inline underline border-bottom to customize underline downstream */
  bottom: calc(var(--_border-bottom-offset, -2) * var(--nve-ref-border-width-md));
  width: var(--_border-bottom-width, 100%);
  height: var(--nve-ref-border-width-md);
  background-color: currentColor;
  transform: translateX(-50%) scaleX(0);
  transform-origin: center;
  transition: transform 0.3s ease;
  will-change: transform;
  border-radius: var(--nve-ref-border-radius-full);
}

:host([container*='inline']:hover) [internal-host]::after,
:host([container*='inline']:focus) [internal-host]::after {
  transform: translateX(-50%) scaleX(1);
}

:host([container*='inline']:state(disabled)) [internal-host]::after {
  display: none;
}

:host([container*='inline']) {
  --text-decoration: none;
}

:host([container*='inline']:hover) {
  --text-decoration: none;
}
